import FormHeader from "@/components/FormHeader"
import InputError from "@/components/InputError"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MultiSelect } from "@/components/ui/multi-select"
import { Switch } from "@/components/ui/switch"
import { listLokasiKuliah } from "@/constants"
import api from "@/lib/axios"
import { LokasiKuliah, Unitkerja } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaPlus } from "react-icons/fa6"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import { useMutation, useQueryClient } from "@tanstack/react-query"

const postData = async (newData: Unitkerja) => {
  const response = await api.post("/master/unit-kerja/store", newData)
  return response.data
}

const patchData = async (newData: Unitkerja) => {
  const response = await api.patch("/master/unit-kerja/update", newData)
  return response.data
}

const deleteData = async (deletedData: { uk_id: string }) => {
  const response = await api({
    url: "/master/unit-kerja/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: Unitkerja | null
  closeModal: () => void
}) {
  const [selectedKelasKuliah, setSelectedKelasKuliah] = useState<string[]>(
    selectedRow?.kelas_kuliah || []
  )
  const [form, setForm] = useState<Unitkerja>({
    uk_id: selectedRow?.uk_id || "",
    nama: selectedRow?.nama || "",
    is_active: selectedRow?.is_active ?? false,
    kode_snpmb: selectedRow?.kode_snpmb || "",
    kode_afirmasi: selectedRow?.kode_afirmasi || "",
    kelas_kuliah: selectedRow?.kelas_kuliah || null,
  })

  const optionsKelasKuliah = listLokasiKuliah.map((value) => {
    return { label: value, value: value.toLowerCase() }
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending, error } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listUnitkerja"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Unitkerja"
          description={`Apakah anda yakin akan menghapus data "${selectedRow?.nama}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Unitkerja" : "Update Data Unitkerja"}
      />
      <div>
        <Label>Nama Unitkerja</Label>
        <Input disabled readOnly value={form.nama} />
      </div>
      <div>
        <Label>Kode SNPMB</Label>
        <Input
          placeholder="Ketik kode snpmb..."
          value={form.kode_snpmb}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              kode_snpmb: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Kode Afirmasi</Label>
        <Input
          placeholder="Ketik kode afirmasi..."
          value={form.kode_afirmasi}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              kode_afirmasi: event.target.value,
            }))
          }
        />
      </div>

      <div>
        <Label>Kelas Kuliah</Label>
        <MultiSelect
          options={optionsKelasKuliah || []}
          onValueChange={(value) => {
            setSelectedKelasKuliah(value)
            setForm((prev) => ({
              ...prev,
              kelas_kuliah: value as LokasiKuliah[],
            }))
          }}
          defaultValue={selectedKelasKuliah}
          placeholder="Pilih Kelas Kuliah"
          variant="inverted"
          animation={2}
          maxCount={3}
        />
        <InputError
          text={error?.response?.data?.errors?.kelas_kuliah?.join(",")}
        />
      </div>

      <div className="flex gap-3">
        <Switch
          id="status"
          checked={form.is_active}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_active: !prev.is_active,
            }))
          }
        />
        <Label htmlFor="status">Status Nonaktif/Aktif</Label>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
